import React from "react";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import * as Accordion from "@radix-ui/react-accordion";
import classNames from "classnames";
import { Flex, Text, Code, Box, Container, Separator, Heading, Badge, Strong } from "@radix-ui/themes";


// Components for the accordion dropdown - the trigger and content
const AccordionTrigger = React.forwardRef<
    HTMLButtonElement,
    React.ComponentPropsWithoutRef<typeof Accordion.Trigger>
>(({ children, className, ...props }, ref) => (
    <Accordion.Header className="AccordionHeader">
        <Accordion.Trigger
            className={classNames("AccordionTrigger", className)}
            {...props}
            ref={ref}
        >
            {children}
            <ChevronDownIcon className="AccordionChevron" aria-hidden />
        </Accordion.Trigger>
    </Accordion.Header>
));
AccordionTrigger.displayName = "AccordionTrigger";

const AccordionContent = React.forwardRef<
    HTMLDivElement,
    React.ComponentPropsWithoutRef<typeof Accordion.Content>
>(({ children, className, ...props }, ref) => (
    <Accordion.Content
        className={classNames("AccordionContent", className)}
        {...props}
        ref={ref}
    >
        <div className="AccordionContentText">{children}</div>
    </Accordion.Content>
));
AccordionContent.displayName = "AccordionContent";

//  Support component that uses the Accordion components - beginning with inline styled CSS for the accordion
const Support = () => (

    <>
        <style>{`
        button, h3 { all: unset; }
        .AccordionRoot {
          border-radius: 6px;
          background-color: transparent;
          box-shadow: 0 2px 10px var(--black-a4);
        }
        .AccordionItem {
          overflow: hidden;
          margin-top: 1px;
        }
        .AccordionItem:first-child {
          margin-top: 0;
          border-top-left-radius: 4px;
          border-top-right-radius: 4px;
        }
        .AccordionItem:last-child {
          border-bottom-left-radius: 4px;
          border-bottom-right-radius: 4px;
        }
        .AccordionItem:focus-within {
          position: relative;
          z-index: 1;
          box-shadow: 0 0 0 2px var(--mauve-12);
        }
        .AccordionHeader {
          display: flex;
        }
        .AccordionTrigger {
          font-family: inherit;
          background-color: var(--mauve-2);
          padding: 0 20px;
          height: 60px;
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 15px;
          line-height: 1.3;
          color: white;
          box-shadow: 0 1px 0 var(--mauve-6);
        }
        .AccordionContent {
          overflow: hidden;
          font-size: 15px;
          color: var(--mauve-11);
          background-color: transparent;
        }
        .AccordionContent[data-state="open"] {
          animation: slideDown 300ms cubic-bezier(0.87, 0, 0.13, 1);
        }
        .AccordionContent[data-state="closed"] {
          animation: slideUp 300ms cubic-bezier(0.87, 0, 0.13, 1);
        }
        .AccordionContentText {
          padding: 15px 20px;
        }
        .AccordionChevron {
          color: green;
          transition: transform 300ms cubic-bezier(0.87, 0, 0.13, 1);
        }
        .AccordionTrigger[data-state="open"] > .AccordionChevron {
          transform: rotate(180deg);
        }
        @keyframes slideDown {
          from { height: 0; }
          to { height: var(--radix-accordion-content-height); }
        }
        @keyframes slideUp {
          from { height: var(--radix-accordion-content-height); }
          to { height: 0; }
        }
      `}</style>

        <Box>
            <Container size="2">
                <Flex direction="column" gap="4" px="4" py="9">

                    <Heading size="8" color="green">How can we help?</Heading>

                    <Accordion.Root
                        className="AccordionRoot"
                        type="single"
                        defaultValue="item-1"
                        collapsible
                    >
                        <Heading size="6">Common Questions</Heading>
                        <Accordion.Item className="AccordionItem" value="item-1">
                            <AccordionTrigger>I previously purchased a subscription but when I open the app it's no longer working. What's happened?</AccordionTrigger>
                            <AccordionContent>
                                <Text as="p" mb="2" >This can be caused by a number of issues. The most common is that you have changed your Google account on the device. If you have multiple Google accounts, please ensure you are logged into the correct one.</Text>

                                <Text as="p" mb="2" >This is also frequently caused by the Google Play service on your phone going out of sync. This can sometimes be fixed by force closing the app and reopening the app, and will almost always be fixed by restarting your phone altogether.</Text>

                                <Text as="p" mb="2" >If you are still having issues, please get in touch with us by using 'Contact Us' in the top right.</Text>
                            </AccordionContent>
                        </Accordion.Item>

                        <Accordion.Item className="AccordionItem" value="item-2">
                            <AccordionTrigger>What are the benefits of the subscription?</AccordionTrigger>
                            <AccordionContent>

                                The free download provides access to today's listings and the next match of a chosen team, competition or channel. A subscription costs less than the price of a pint and unlocks all app features including:
                                <ul>
                                    <li><Text as="span"><Strong>Access All Matches:</Strong> Access all future match listings and plan your viewing in advance</Text></li>
                                    <li><Text as="span"><Strong>Guide:</Strong> Your personalised listings of upcoming matches filtered to the teams, competitions, channels, and matches you follow.</Text></li>
                                    <li><Text as="span"><Strong>Hide Matches:</Strong> Hide matches, teams, competitions and channels from appearing across the app.</Text></li>
                                    <li><Text as="span"><Strong>Match Alerts:</Strong> Optionally setup notifications ahead of the match broadcast to ensure you never miss a game again.</Text></li>

                                </ul>
                                Behind the app is an independent, self-funded team of two. Support us with a subscription and help support ongoing development and future improvement of the app.

                            </AccordionContent>
                        </Accordion.Item>

                        <Accordion.Item className="AccordionItem" value="item-3">
                            <AccordionTrigger>What is the Guide functionality?</AccordionTrigger>
                            <AccordionContent>
                                <Text as="p" mb="2" >Your Guide is a personalised listing of your upcoming matches. Your Guide is based on the matches, teams, competitions and channels that you follow in the app. </Text>
                                
                                <Text as="p" mb="2" >If you have chosen to hide specific teams, competitions, channels or matches, then these matches won't appear in the guide. The guide therefore allows you to fully tailor the matches in the app into what is relevant for you.</Text>
                                
                                <Text as="p" mb="2" >Your guide will be synchronised across all the devices you use. </Text>

                                <Text as="p" mb="2" ><Strong>To get started:</Strong> Tap the follow chip on any match, team, competition or channel. All of the upcoming matches from who you follow will appear together on the Guide page.</Text>
                            </AccordionContent>
                        </Accordion.Item>

                        <Accordion.Item className="AccordionItem" value="item-4">
                            <AccordionTrigger>How do I watch live matches?</AccordionTrigger>
                            <AccordionContent>
                                The app provides the most up-to-date, extensive and accurate listings of matches shown live in the UK. The app does not provide any direct facility for watching or streaming matches. You will need access or a subscription to the relevant broadcaster or platform to watch the matches listed.
                            </AccordionContent>
                        </Accordion.Item>

                        <Accordion.Item className="AccordionItem" value="item-5">
                            <AccordionTrigger>I have feedback or an idea to improve the app. How can I contact you?</AccordionTrigger>
                            <AccordionContent>
                                Awesome, we love new ideas! Click 'Contact Us' at the top right of the page to get in touch.
                            </AccordionContent>
                        </Accordion.Item>

                        <Heading size="6" my="3">Subscriptions</Heading>
                        <Accordion.Item className="AccordionItem" value="item-6">
                            <AccordionTrigger>How do I manage my subscription?</AccordionTrigger>
                            <AccordionContent>
                                Your subscription is managed via the Google Play store. You can find the link to manage your subscription through the Settings screen in the app. Alternatively, open the Google Play Store app, click the Menu icon, click Subscriptions and then select the Live Football On TV app.
                            </AccordionContent>
                        </Accordion.Item>

                        <Accordion.Item className="AccordionItem" value="item-7">
                            <AccordionTrigger>How do I cancel my subscription?</AccordionTrigger>
                            <AccordionContent>
                                <Text as="p" mb="2" >We're sorry :( Before you go, we would love to hear why you're leaving so we can improve the app for you. Please use the 'Contact Us' button in the top right and let us know what we can do better. </Text>
                                <Text as="p" mb="2" >Subscriptions can be canceled directly through the Google Play app. Alternatively, monthly subscriptions can be temporarily paused if you wish to resume the subscription at a later date.</Text>
                            </AccordionContent>
                        </Accordion.Item>

                        <Accordion.Item className="AccordionItem" value="item-8">
                            <AccordionTrigger>How do I transfer my subscription between iOS and Android?</AccordionTrigger>
                            <AccordionContent>
                                <Text as="p" mb="2" >Unfortunately this is not possible. The subscriptions for each app are handled separately between Google Play and Apple's App Store. Google and Apple do not allow the subscriptions to be used in a multi-platform way. </Text>
                                <Text as="p" mb="2" >Nevertheless, please give us a message via the Contact Us button above. If you haven't already taken out a free trial on the Android app, we will be happy to unlock an extended version for you.</Text>
                            </AccordionContent>
                        </Accordion.Item>

                        <Heading size="6" my="3">Match Information</Heading>
                        <Accordion.Item className="AccordionItem" value="item-9">
                            <AccordionTrigger>The app isn't updating with new match information. What can I try?</AccordionTrigger>
                            <AccordionContent>


                                <Text as="p" mb="2" >The app data refreshes automatically when you open the app, and infrequently in the background. If you have difficulties refreshing the app data, try the following steps in this order:</Text>
                                <ol>
                                    <li>Check you have an active Internet connection. Are you able to load information in other apps?</li>
                                    <li>On the home page, swipe down from the top of the match list until you see the loading spinner.</li>
                                    <li>Force-close the app from your recent apps screen and re-open the app.</li>
                                    <li>Clear the app cache.</li>
                                    <li>Clear the app data.</li>
                                </ol>
                                    
                                <Text as="p" mb="2" >Still having issues? Please let us know by using 'Contact Us' in the top right and we will figure this out together.</Text>

                            </AccordionContent>
                        </Accordion.Item>

                        <Accordion.Item className="AccordionItem" value="item-10">
                            <AccordionTrigger>I can't see a match listed in the app. Why is this?</AccordionTrigger>
                            <AccordionContent>


                                <Text as="p" mb="2" >In the vast majority of cases this is because the match is not being broadcast in the UK, or the broadcasters for this match have not been announced yet.</Text>

                                <Text as="p" mb="2">Extremely rarely we may miss the match. Please let us know by using 'Contact Us' in the top right.</Text>

                            </AccordionContent>
                        </Accordion.Item>

                        <Accordion.Item className="AccordionItem" value="item-11">
                            <AccordionTrigger>Can I sychronise match information to my Calendar of choice?</AccordionTrigger>
                            <AccordionContent>


                                <Text as="p" mb="2">Unfortunately on Android this is not possible yet due to the complexity of the technical setup. We are planning a future solution, though, as this is one of our top requested features.</Text>

                                <Text as="p" mb="2">For now, you can save each match individually to your calendar and if you're looking to setup notifications ahead of games, you can tap the 'Notify Me' button on individual games, or tap the notification chip on a team, channel or competition to get notified for all their games. We will notify you just before the broadcast starts.</Text>

                            </AccordionContent>
                        </Accordion.Item>

                        <Heading size="6" my="3">Notifications</Heading>
                        <Accordion.Item className="AccordionItem" value="item-12">
                            <AccordionTrigger>How do I setup match alerts?</AccordionTrigger>
                            <AccordionContent>
                                Tap the 'Notify Me' button on individual games, or tap the notification chip on a team, channel or competition. We will notify you just before the broadcast starts.
                            </AccordionContent>
                        </Accordion.Item>

                        <Accordion.Item className="AccordionItem" value="item-13">
                            <AccordionTrigger>Why didn't I receive a notification?</AccordionTrigger>
                            <AccordionContent>


                                <Text as="p" mb="2">Simple troubleshooting steps:</Text>
                                <ol>
                                    <li>Check that you have notifications enabled for the app in your phone's settings.</li>
                                    <li>Ensure that you are not in Do Not Disturb/Silent mode.</li>
                                    <li>Try tapping the 'Test Notification' option from the Settings page and waiting 10 seconds.</li>
                                </ol>
                                <Text as="p" mb="2">If you still don't see any notifications, please tap 'Contact Us' in the top right and tell us what you've tried.</Text>
                            </AccordionContent>
                        </Accordion.Item>

                        <Accordion.Item className="AccordionItem" value="item-14">
                            <AccordionTrigger>I received a notification but not in time. Why?</AccordionTrigger>
                            <AccordionContent>
                                <Text as="p" mb="2">We automatically attempt to deliver notifications 15 minutes before kickoff (by default, if you haven't changed the default timing). In most cases this will be successful.</Text>

                                <Text as="p" mb="2">However, Android and certain phone manufacturers implement battery saving 'functionality' which can prevent notifications coming through until a later stage. Unfortunately there is nothing we can do about this – please see dontkillmyapp.com for further details.</Text>
                            </AccordionContent>
                        </Accordion.Item>

                        <Heading size="6" my="3">Other</Heading>
                        <Accordion.Item className="AccordionItem" value="item-15">
                            <AccordionTrigger>Who built this?</AccordionTrigger>
                            <AccordionContent>


                                <Text as="p" mb="2">The Live Football on TV Android app is the creation of Patrick Millais and Neil Hale.</Text>

                                <Text as="p" mb="2">Patrick looks after the design, development and maintenance of the Android app, while Neil ensures the listings meet our mission of being the most up-to-date, accurate and extensive of live football in the UK. This often involves manual updates of listings – every single match is hand-checked for accuracy.</Text>

                                <Text as="p" mb="2">We are totally self-funded – no large corporations involved – and this is our side project! The revenue from every subscription goes directly towards ensuring we can put our time and effort into the app's future.</Text>

                                <Text as="p" mb="2">If you've got ideas on how we can improve the app, or have general feedback, please do get in touch using the 'Contact Us' option in the top right of this page.</Text>

                            </AccordionContent>
                        </Accordion.Item>

                        <Accordion.Item className="AccordionItem" value="item-16">
                            <AccordionTrigger>How do I delete my account and all my data?</AccordionTrigger>
                            <AccordionContent>
                                <Text as="p" mb="2">You can request that we delete all data associated with your account and the Live Football On TV app.</Text>

                                <Text as="p" mb="2">The easiest way to delete your account is through the app:</Text>
                                <ul>
                                    <li>Open the Settings screen by tapping your profile photo on the Explore screen.</li>
                                    <li>Tap the 'Manage Account' banner at the top of the Settings screen</li>
                                    <li>Tap 'Close Account' to permanently delete all your data from the service</li>
                                    <li>If you do not have the app installed, you can request your data is deleted by contacting us here</li>

                                </ul>

                                <Text as="p" mb="2">Your account will be deleted from our active databases immediately. Your personal data may be retained in our backup system until it is overwritten according to the service’s backup schedule – for a maximum of 60 days. Note: In practice your personal data will be removed from the backup system in a matter of days.</Text>
                            </AccordionContent>
                        </Accordion.Item>

                    </Accordion.Root>


                </Flex>
                
                <Separator my="2" size="4" />
            
                <Flex direction="row" my="3">
                    <Text size="2">Last Updated: </Text> 	<Badge color="green">7 August 2025</Badge>
                </Flex>
            </Container>
        </Box>
    </>


);

export default Support;
